import React, { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ClearIcon } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import { useGetDeliveryDriversList } from "src/api/delivery";
import TableActions from "./table-actions";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate } from "react-router-dom";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { useGetLocationsList } from "src/api/locations";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";

const tableHeadText = [
  "National ID",
  "English Name",
  "Arabic Name",
  "Location",
  "Email",
  "Mobile Number",
  "Actions",
];

const ActiveDriversTable = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [tempLocation, setTempLocation] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [auth] = useRecoilState(authState);
  const { permissions } = auth;

  const filters = {
    status: "active" as "active",
    ...(searchBy && { search: searchBy }),
    locationId: location?.id,
  };

  // APIs
  const { data, isLoading, refetch } = useGetDeliveryDriversList({
    ...filters,
    page: page + 1,
    limit: rowsPerPage,
  });
  const { data: locationsData } = useGetLocationsList({ hasDrivers: true });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempLocation(newValue);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  const handleResetFilers = () => {
    setSearchBy("");
    setLocation(null);

    setTempSearchBy("");
    setTempLocation(null);
  };

  const handleApply = () => {
    setSearchBy(tempSearchBy);
    setLocation(tempLocation);
  };

  const hasData = data && data?.data?.length > 0;

  const handleNavigate = () => {
    navigate("/delivery/active/add");
  };

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  //return to 1st page after filters change
  useEffect(() => {
    setPage(0);
  }, [location, searchBy]);

  useEffect(() => {
    if (data) {
      setTotalCount(data?.meta.total);
    }
  }, [data]);

  return (
    <>
      {permissions?.delivery.create ? (
        <Stack mb={2.2} alignItems={"flex-end"}>
          <Button
            variant="contained"
            sx={{ gap: 0.5 }}
            onClick={handleNavigate}
          >
            <AddBoxIcon fontSize="small" sx={{ opacity: 0.7 }} />
            Add New Driver
          </Button>
        </Stack>
      ) : null}
      {/* filters */}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          {/* search */}
          <TextField
            size="small"
            placeholder="Search"
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            sx={{ width: { xs: "100%", sm: "250px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* locations */}
          <CustomAutocomplete
            size="small"
            fullWidth
            disablePortal
            id="locations-auto-select"
            options={sortedLocations}
            key={tempLocation?.id ?? "loc-autocomplete"}
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            value={tempLocation}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
            sx={{ maxWidth: "220px" }}
          />
        </Stack>
        <Stack direction={"row"}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>
      <Paper
        sx={{
          bgcolor: "#1A1A1A",
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: { xs: "15px", md: "30px" },
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {tableHeadText.map((item) => (
                  <TableCell
                    key={item}
                    sx={{
                      fontWeight: 600,
                      color: "#A1A5B7",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={rowsPerPage} cellsLength={7} />
            ) : (
              <>
                {hasData ? (
                  <TableBody>
                    {data?.data.map((row, index: number) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.nationalId}</TableCell>
                        <TableCell>{row.nameEn}</TableCell>
                        <TableCell>{row.nameAr}</TableCell>
                        <TableCell>
                          {row.locations.map((loc) => loc.nameEn).join(", ")}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phoneNumber}</TableCell>
                        <TableCell>
                          <TableActions
                            rowId={row.id as string}
                            refetch={refetch}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : null}
              </>
            )}
          </Table>

          {!hasData && !isLoading && <NoData />}

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
          />
        </TableContainer>
      </Paper>
    </>
  );
};
export default ActiveDriversTable;
